<template>
    <div class="task-report" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="select-workflow">
                    <label for="workflow-select">{{ $t("report.selectWorkflow") }}:</label>
                    <select id="workflow-select" v-model="selectedWorkflow">
                        <option :value="null">{{ $t("report.selectWorkflow") }}</option>
                        <option v-for="workflow in workflowOptions" :key="workflow._id" :value="workflow._id">
                            {{ workflow.title }}
                        </option>
                    </select>
                </div>

                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div v-if="selectedWorkflow !== null">
            <DoubleBarChart
                :title="$t('report.distributionOfTasks')"
                :xAxisData="getXAxisData()"
                :yAxisData="getYAxisData()"
                :series="getSeries()"
                v-if="!tableMode"
            />
        </div>

        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard :title="$t('report.mostMissioned')" :value="getStats().MaxAssignee" />
            <StatCard :title="$t('report.leastTask')" :value="getStats().MinAssignee" />
            <StatCard :title="$t('report.totalNumberTasks')" :value="getStats().totalAssignee" />
        </div>
        <div class="stats">
            <StatCard v-for="pipeline in pipelineNames" :key="pipeline._id" :title="pipeline.name"
                      :value="pipeline.count" />
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import fscreen from "fscreen";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import { mapState } from "vuex";

const log = log2("TaskReport");

export default Vue.extend({
    name: "TaskReport",
    components: { DoubleBarChart, TableView, IconButton, StatCard },
    props: {
        filters: {
            type: Object,
            default: () => ({
                assignees: [],
                startDate: new Date().toISOString(),
                endDate: new Date().toISOString(),
            }),
        },
    },
    data() {
        return {
            tableMode: false,
            fullScreen: false,
            data: [],
            pipelineNames: [],
            loading: true,
            selectedWorkflow: null,
        };
    },
    computed: {
        ...mapState({
            workflows: (state) => state.tasks.workflows,
        }),
        workflowOptions() {
            return Object.values(this.workflows);
        },
    },
    methods: {
        async fetch() {
            log("fetchData", this.filters);
            this.loading = true;
            const filtersWithWorkflow = {
                ...this.filters,
                workflowId: this.selectedWorkflow,
            };
            await REPORT_API.taskOverall(filtersWithWorkflow)
                .then((res) => {
                    log("taskOverall data fetched", res);
                    this.data = res.data;
                    this.pipelineNames = res.pipelineNames;
                })
                .catch((error) => {
                    console.error(this.$t("report.erorFetchingTask"), error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".task-report");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                { title: this.$t("report.pipelinePhase"), dataIndex: "pipelineStage", key: "pipelineStage" },
                { title: this.$t("report.status"), dataIndex: "status", key: "status" },
                { title: this.$t("report.numberOfTasks"), dataIndex: "count", key: "count" },
            ];
        },
        getRows() {
            return this.data.map((item) => ({
                pipelineStage: item.pipelineStage,
                status: item.status,
                count: item.count,
            }));
        },
        getStats() {
            const items = this.data || [];
            const MaxAssignee = _.maxBy(items, "count")?.pipelineStage || this.$t("report.insufficientData");
            const MinAssignee = _.minBy(items, "count")?.pipelineStage || this.$t("report.insufficientData");
            const totalAssignee = _.sumBy(items, "count") || 0;

            return { MaxAssignee, MinAssignee, totalAssignee };
        },
        getYAxisData() {
            const items = this.data || [];
            return {
                type: "category",
                data: _.uniq(items.map((item) => item.pipelineStage)),
                axisLabel: { interval: 0, rotate: 30 },
                splitLine: { show: false },
            };
        },
        getXAxisData() {
            const items = this.data || [];
            const max = _.max(items.map((x) => x.count || 0)) || 0;
            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: { show: false },
                axisLabel: { rotate: -30 },
            };
        },
        getSeries() {
            const items = this.data || [];
            const groupedItems = _.groupBy(items, "pipelineStage");
            const seriesData = _.map(groupedItems, (stageItems, stageName) => {
                const totalCount = _.sumBy(stageItems, "count");
                return {
                    name: stageName,
                    type: "bar",
                    stack: "total",
                    label: { show: true, position: "inside" },
                    data: [totalCount],
                };
            });
            return [{
                name: this.$t("report.numberOfTasks"),
                type: "bar",
                data: Object.keys(groupedItems).map(stageName => ({
                    name: stageName,
                    value: _.sumBy(groupedItems[stageName], "count"),
                })),
            }];
        },

    },
    created() {
        this.fetch();
    },
    watch: {
        filters: {
            handler() {
                this.fetch();
            },
            deep: true,
        },
        selectedWorkflow(newWorkflow) {
            this.filters.workflowId = newWorkflow;
            this.fetch();
        },
    },
});
</script>


<style lang="scss">
.task-report {
    .title {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart {
            height: calc(100vh - 200px);
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .buttons {
            display: flex;

            .icon-button {
                padding: 5px;
                width: 40px;
                height: 34px;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid var(--color-border-default);

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }

                &.selected {
                    background-color: var(--color-bg-info-low-hover);

                    i {
                        color: rgb(55, 162, 255);
                    }
                }
            }
        }
    }

    .stats {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 20px;
    }

    .statsOne {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 20px;
    }
}

.select-workflow {
    width: 100%;
    text-align: center;
    margin: auto;
    margin-left: 80px;
    margin-bottom: 20px;

    label {
        display: block;
        margin-bottom: 8px;
        color: #333;
        font-size: 16px;
        font-weight: bold;
    }

    select {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: #fff;
        box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
        font-size: 14px;
        color: #333;

        &:focus {
            border-color: #007bff;
            outline: none;
        }
    }

    option {
        padding: 8px 12px;
    }
}
</style>
